import React, { useState, useEffect, useRef } from 'react';
import { ResizableBox } from 'react-resizable';
import { X } from 'lucide-react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { SpecialZoomLevel } from '@react-pdf-viewer/core';
import { renderAsync } from 'docx-preview';
import '@react-pdf-viewer/core/lib/styles/index.css';
import 'react-resizable/css/styles.css';
import '../styles/FileSidePanel.css';

const FileSidePanel = ({
  isOpen,
  onClose,
  fileName,
  fileUrl,
  initialPage = 0,
  initialWidth = 500,
  minWidth = 300,
  maxWidth = 800,
  scale = 1.2,
  onWidthChange,
}) => {
  const [panelWidth, setPanelWidth] = useState(initialWidth);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(0);
  const wordContainerRef = useRef(null);
  const [wordPages, setWordPages] = useState([]);
  
  const isWordFile = fileName?.toLowerCase().endsWith('.docx') || fileName?.toLowerCase().endsWith('.doc');

  useEffect(() => {
    setCurrentPage(initialPage);
  }, [fileUrl, initialPage]);

  useEffect(() => {
    onWidthChange?.(panelWidth);
  }, [panelWidth, onWidthChange]);

  useEffect(() => {
    const loadWordDocument = async () => {
      if (isWordFile && wordContainerRef.current && fileUrl) {
        try {
          const response = await fetch(fileUrl);
          const blob = await response.blob();
          
          if (wordContainerRef.current) {
            wordContainerRef.current.innerHTML = '';
          }
          
          await renderAsync(blob, wordContainerRef.current, null, {
            className: 'docx-preview',
            inWrapper: false,
            ignoreWidth: false,
            ignoreHeight: false,
            ignoreFonts: false,
            breakPages: true,
            useBase64URL: true,
            defaultFont: {
              family: 'Arial',
              url: null
            },
            renderHeaders: true,
            renderFooters: true,
            pageFitWidth: false,
            maxWidth: panelWidth - 40,
            enableGraying: false,
            enableMathML: false,
            enableLanguageInformation: false
          });

          const pages = wordContainerRef.current.querySelectorAll('.docx > section');
          setWordPages(Array.from(pages));
          setTotalPages(pages.length);
          
          const container = wordContainerRef.current;
          const handleScroll = () => {
            const scrollTop = container.scrollTop;
            const containerHeight = container.clientHeight;
            
            let maxVisiblePage = 0;
            let maxVisibleArea = 0;
            
            pages.forEach((page, index) => {
              const rect = page.getBoundingClientRect();
              const containerRect = container.getBoundingClientRect();
              const visibleHeight = Math.min(rect.bottom, containerRect.bottom) - 
                                  Math.max(rect.top, containerRect.top);
              
              if (visibleHeight > maxVisibleArea) {
                maxVisibleArea = visibleHeight;
                maxVisiblePage = index;
              }
            });
            
            setCurrentPage(maxVisiblePage);
          };
          
          container.addEventListener('scroll', handleScroll);
          return () => container.removeEventListener('scroll', handleScroll);
        } catch (error) {
          console.error('Error rendering Word document:', error);
        }
      }
    };

    loadWordDocument();
  }, [fileUrl, isWordFile, panelWidth]);

  const navigateToPage = (pageIndex) => {
    if (isWordFile && wordPages[pageIndex]) {
      wordPages[pageIndex].scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (!isOpen) return null;

  const handleResize = (event, { size }) => {
    setPanelWidth(size.width);
  };

  const containerStyle = {
    position: 'fixed',
    right: 0,
    top: '64px',
    height: 'calc(100vh - 64px)',
    background: 'white',
    boxShadow: '-2px 0 5px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1000,
    width: `${panelWidth}px`
  };

  return (
    <ResizableBox
      width={panelWidth}
      height={window.innerHeight - 64}
      minConstraints={[minWidth, window.innerHeight - 64]}
      maxConstraints={[maxWidth, window.innerHeight - 64]}
      axis="x"
      handle={<div className="resize-handle-left" />}
      onResize={handleResize}
      resizeHandles={['w']}
      style={containerStyle}
    >
      <div className="file-side-panel">
        <div className="file-side-panel-header">
          <h3>{fileName}</h3>
          <div className="header-right">
            {totalPages > 0 && (
              <div className="page-counter">
                Page {currentPage + 1}/{totalPages}
              </div>
            )}
            <button onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="file-side-panel-content">
          {isWordFile ? (
            <>
              <div style={{
                backgroundColor: '#FEF3C7',
                color: '#92400E',
                padding: '8px 12px',
                fontSize: '14px',
                textAlign: 'center',
                width: '100%',
                marginBottom: '8px'
              }}>
                Word doc preview is in beta and may have bugs.
              </div>
              <div 
                ref={wordContainerRef}
                style={{ 
                  width: `${panelWidth - 40}px`,
                  height: '100%', 
                  overflow: 'auto',
                  padding: '20px 0',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  margin: 0,
                  maxWidth: '100%'
                }}
                className="word-preview-container"
              />
            </>
          ) : (
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <div style={{ width: '100%', height: '100%' }}>
                <Viewer
                  fileUrl={fileUrl}
                  defaultScale={SpecialZoomLevel.PageFit}
                  initialPage={currentPage}
                  onPageChange={e => setCurrentPage(e.currentPage)}
                  onDocumentLoad={e => setTotalPages(e.doc.numPages)}
                />
              </div>
            </Worker>
          )}
        </div>
      </div>
    </ResizableBox>
  );
};

export default FileSidePanel; 